/**
 * Dream Graph Story Traversal Engine
 *
 * The core execution engine for play mode and simulation. Handles:
 * - Graph navigation and node traversal
 * - Condition evaluation to branch story flow
 * - Instruction execution to modify game state
 * - State management and history tracking
 * - Playthrough saving and replay
 *
 * The engine works with the graph structure and maintains complete state
 * to support features like step-back, variable inspection, and coverage reporting.
 *
 * **Design:** The engine is library-independent and can be used standalone,
 * making it suitable for game-side runtimes as well.
 *
 * @module playMode/TraversalEngine
 */

import { parseScript } from '../scripting/parser';
import { evaluate } from '../scripting/interpreter';
import type { VariableStore, EvaluationContext } from '../scripting/types';

/**
 * Story Node Type Definition
 *
 * A simplified view of a graph node used by the traversal engine.
 * Includes only the information needed for playthrough (not rendering).
 */
export interface StoryNode {
  id: string;
  type: string;
  data: Record<string, unknown>;
  parentId?: string; // For nested graphs
}

/**
 * Story Connection Definition
 *
 * Represents an edge in the graph with optional condition gating.
 * The `sourceHandle` and `targetHandle` are used to identify which
 * output port of the source node connects to which input port of the target.
 */
export interface StoryConnection {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
  data?: {
    condition?: string; // Optional condition for choice edges
    label?: string; // Menu text for dialogue choices
  };
}

/**
 * Story Graph Definition
 *
 * A complete story graph structure including all nodes, connections,
 * and the entry point for starting playback.
 */
export interface StoryGraph {
  id: string;
  nodes: Map<string, StoryNode>;
  connections: StoryConnection[];
  entryNodeId?: string;
}

/**
 * Playthrough Step
 *
 * Records a single moment in a playthrough:
 * - Which node was visited
 * - What choices were available
 * - Which choice was taken
 * - The state after executing the node
 *
 * Used for saving/loading and step-back functionality.
 */
export interface PlaythroughStep {
  nodeId: string;
  nodeType: string;
  choices?: {
    label: string;
    targetId: string;
    targetLabel: string;
    conditionMet: boolean;
  }[];
  chosenConnectionId?: string;
  stateBefore: VariableStore;
  stateAfter: VariableStore;
  timestamp: number;
}

/**
 * Traversal Result
 *
 * The outcome of a traversal action (e.g., moving to the next node).
 * Contains the new state and information about what happened.
 */
export interface TraversalResult {
  success: boolean;
  currentNodeId: string;
  nextNodeId?: string;
  choices?: {
    label: string;
    targetId: string;
    targetLabel: string;
    conditionMet: boolean;
  }[];
  error?: string;
  stepData?: PlaythroughStep;
}

/**
 * Playthrough State
 *
 * Represents the complete state of a playthrough:
 * - Current position in the story
 * - All variable values
 * - History of visited nodes
 * - Steps for save/replay
 *
 * This is the state saved when a playthrough is saved.
 */
export interface PlaythroughState {
  id: string;
  name: string;
  graphId: string;
  startNodeId: string;
  currentNodeId: string;
  variables: VariableStore;
  steps: PlaythroughStep[];
  isComplete: boolean;
  createdAt: number;
  completedAt?: number;
}

/**
 * Traversal Engine Configuration
 *
 * Options that control engine behavior:
 * - `maxSteps`: Prevent infinite loops
 * - `trackHistory`: Whether to save playthrough history
 * - `builtins`: Custom built-in functions for scripts
 */
export interface TraversalConfig {
  maxSteps?: number;
  trackHistory?: boolean;
  builtins?: Record<string, (...args: unknown[]) => unknown>;
}

/**
 * Dream Graph Story Traversal Engine
 *
 * Core class for story playthrough and simulation.
 * Maintains playthrough state and handles all story progression logic.
 *
 * **Typical usage:**
 * ```typescript
 * const engine = new TraversalEngine(graph, {
 *   trackHistory: true,
 *   maxSteps: 1000,
 * });
 *
 * let result = engine.startPlaythrough('entry-node-id');
 *
 * while (!result.success || result.choices?.length === 0) {
 *   // Evaluate conditions and present choices to player
 *   if (result.choices?.length) {
 *     result = engine.makeChoice(result.choices[0].targetId);
 *   } else {
 *     break;
 *   }
 * }
 *
 * const playthrough = engine.getCurrentPlaythrough();
 * engine.savePlaythrough('My Story Run');
 * ```
 */
export class TraversalEngine {
  private graph: StoryGraph;
  private config: Required<TraversalConfig>;
  private currentState: PlaythroughState | null = null;
  private stepCount = 0;
  private visitedNodeIds = new Set<string>();

  constructor(graph: StoryGraph, config: TraversalConfig = {}) {
    this.graph = graph;
    this.config = {
      maxSteps: config.maxSteps ?? 10000,
      trackHistory: config.trackHistory ?? true,
      builtins: config.builtins ?? {},
    };
  }

  /**
   * Start a new playthrough from a specified node
   *
   * Initializes playthrough state with empty variables and begins traversal.
   * The first node is typically an Entry node or the story's opening fragment.
   *
   * @param startNodeId The node ID to start from
   * @param initialVariables Optional initial variable state
   * @returns Traversal result with the first node's state
   */
  startPlaythrough(
    startNodeId: string,
    initialVariables: VariableStore = {}
  ): TraversalResult {
    this.stepCount = 0;
    this.visitedNodeIds.clear();

    this.currentState = {
      id: `playthrough-${Date.now()}`,
      name: '',
      graphId: this.graph.id,
      startNodeId,
      currentNodeId: startNodeId,
      variables: { ...initialVariables },
      steps: [],
      isComplete: false,
      createdAt: Date.now(),
    };

    return this.processNode(startNodeId);
  }

  /**
   * Make a choice and advance the story
   *
   * Selects an outgoing edge from the current node and moves to the target.
   * Handles condition evaluation and instruction execution automatically.
   *
   * @param targetNodeId The ID of the node to move to
   * @returns Traversal result with the target node's state
   */
  makeChoice(targetNodeId: string): TraversalResult {
    if (!this.currentState) {
      return {
        success: false,
        currentNodeId: '',
        error: 'No active playthrough',
      };
    }

    if (this.stepCount >= this.config.maxSteps) {
      return {
        success: false,
        currentNodeId: this.currentState.currentNodeId,
        error: `Maximum steps (${this.config.maxSteps}) exceeded`,
      };
    }

    this.stepCount++;

    // Find the connection from current to target
    const connection = this.graph.connections.find(
      (c) => c.source === this.currentState!.currentNodeId && c.target === targetNodeId
    );

    if (!connection) {
      return {
        success: false,
        currentNodeId: this.currentState.currentNodeId,
        error: `No connection from ${this.currentState.currentNodeId} to ${targetNodeId}`,
      };
    }

    // Check if the connection has a condition (for gated choices)
    if (connection.data?.condition) {
      const conditionMet = this.evaluateCondition(connection.data.condition);
      if (!conditionMet) {
        return {
          success: false,
          currentNodeId: this.currentState.currentNodeId,
          error: 'Condition not met for this choice',
        };
      }
    }

    this.currentState.currentNodeId = targetNodeId;
    return this.processNode(targetNodeId);
  }

  /**
   * Process the current node
   *
   * This is the main node processing logic:
   * 1. Execute the node (instructions, dialogue setup, etc.)
   * 2. Determine what comes next (choice point, auto-advance, etc.)
   * 3. Return the traversal result
   *
   * Different node types are handled specially:
   * - Fragment: Auto-advances to next node or presents choices
   * - Hub: Presents available choices to the player
   * - Condition: Evaluates and branches
   * - Instruction: Executes script and continues
   * - Container/Entry/Exit: Navigation handling
   */
  private processNode(nodeId: string): TraversalResult {
    if (!this.currentState) {
      return {
        success: false,
        currentNodeId: nodeId,
        error: 'No active playthrough',
      };
    }

    const node = this.graph.nodes.get(nodeId);
    if (!node) {
      return {
        success: false,
        currentNodeId: nodeId,
        error: `Node not found: ${nodeId}`,
      };
    }

    // Track visited nodes for coverage reporting
    this.visitedNodeIds.add(nodeId);

    // Record state before processing
    const stateBefore = { ...this.currentState.variables };

    // Process node based on type
    switch (node.type) {
      case 'fragment':
      case 'dialogue':
      // A tilemap_ref node is a pass-through in the story graph (_docs/tilemap/04
      // §4: "the simulator treats tilemap_ref as pass-through") — same
      // auto-advance/choice-branching shape as a fragment, just with no
      // dialogue text of its own. Presentation-layer code may render the
      // referenced map's thumbnail as a "scene slate" while paused here
      // (see PresentationMode.tsx), but the traversal itself doesn't need to
      // know or care that this node points at a tile map rather than text.
      case 'tilemap_ref':
        return this.processFragment(nodeId, node);

      case 'hub':
        return this.processHub(nodeId, node);

      case 'condition':
        return this.processCondition(nodeId, node);

      case 'instruction':
        return this.processInstruction(nodeId, node);

      case 'entry':
        return this.processEntry(nodeId, node);

      case 'exit':
        return this.processExit(nodeId, node);

      default:
        return {
          success: true,
          currentNodeId: nodeId,
        };
    }
  }

  /**
   * Process a Fragment node
   *
   * Fragments are story content nodes that typically:
   * - Display text to the player
   * - Have one input and one or more outputs (choices)
   * - Auto-advance if there's exactly one output
   *
   * If the fragment has multiple outgoing connections, they're presented as choices.
   */
  private processFragment(nodeId: string, node: StoryNode): TraversalResult {
    if (!this.currentState) {
      return {
        success: false,
        currentNodeId: nodeId,
        error: 'No active playthrough',
      };
    }

    // Find outgoing connections from this node
    const outgoing = this.graph.connections.filter((c) => c.source === nodeId);

    if (outgoing.length === 0) {
      // No outgoing connections - story ends here
      this.currentState.isComplete = true;
      this.currentState.completedAt = Date.now();
      return {
        success: true,
        currentNodeId: nodeId,
      };
    }

    if (outgoing.length === 1) {
      // Single outgoing - auto-advance
      return this.makeChoice(outgoing[0].target);
    }

    // Multiple outgoings - present as choices
    const choices = outgoing.map((conn) => {
      const targetNode = this.graph.nodes.get(conn.target);
      return {
        label: conn.data?.label || targetNode?.data.name || 'Continue',
        targetId: conn.target,
        targetLabel: targetNode?.data.name || 'Unnamed',
        conditionMet: !conn.data?.condition || this.evaluateCondition(conn.data.condition),
      };
    });

    return {
      success: true,
      currentNodeId: nodeId,
      choices,
    };
  }

  /**
   * Process a Hub node
   *
   * Hubs are choice points that present all outgoing connections as choices.
   * They don't auto-advance like fragments.
   */
  private processHub(nodeId: string, node: StoryNode): TraversalResult {
    const outgoing = this.graph.connections.filter((c) => c.source === nodeId);

    const choices = outgoing.map((conn) => {
      const targetNode = this.graph.nodes.get(conn.target);
      return {
        label: conn.data?.label || targetNode?.data.name || 'Choice',
        targetId: conn.target,
        targetLabel: targetNode?.data.name || 'Unnamed',
        conditionMet: !conn.data?.condition || this.evaluateCondition(conn.data.condition),
      };
    });

    return {
      success: true,
      currentNodeId: nodeId,
      choices: choices.length > 0 ? choices : undefined,
    };
  }

  /**
   * Process a Condition node
   *
   * Evaluates the condition expression and branches to true/false output.
   * The condition determines which path the story takes automatically.
   */
  private processCondition(nodeId: string, node: StoryNode): TraversalResult {
    if (!this.currentState) {
      return {
        success: false,
        currentNodeId: nodeId,
        error: 'No active playthrough',
      };
    }

    const condition = node.data.expression as string | undefined;
    if (!condition) {
      return {
        success: false,
        currentNodeId: nodeId,
        error: 'Condition expression is empty',
      };
    }

    const conditionMet = this.evaluateCondition(condition);

    // Find the appropriate output handle (true or false)
    const outputHandle = conditionMet ? 'true' : 'false';
    const nextConnection = this.graph.connections.find(
      (c) => c.source === nodeId && c.sourceHandle === outputHandle
    );

    if (!nextConnection) {
      return {
        success: false,
        currentNodeId: nodeId,
        error: `No ${outputHandle} branch found for condition`,
      };
    }

    return this.makeChoice(nextConnection.target);
  }

  /**
   * Process an Instruction node
   *
   * Executes the instruction script (which modifies variables)
   * and then auto-advances to the next node.
   */
  private processInstruction(nodeId: string, node: StoryNode): TraversalResult {
    if (!this.currentState) {
      return {
        success: false,
        currentNodeId: nodeId,
        error: 'No active playthrough',
      };
    }

    const script = node.data.script as string | undefined;
    if (script) {
      const result = this.executeInstruction(script);
      if (!result.success) {
        return {
          success: false,
          currentNodeId: nodeId,
          error: result.error,
        };
      }
    }

    // Auto-advance to the next node
    const nextConnection = this.graph.connections.find((c) => c.source === nodeId);
    if (!nextConnection) {
      this.currentState.isComplete = true;
      this.currentState.completedAt = Date.now();
      return {
        success: true,
        currentNodeId: nodeId,
      };
    }

    return this.makeChoice(nextConnection.target);
  }

  /**
   * Process an Entry node
   *
   * Entry nodes are starting points for nested graphs.
   * They auto-advance to their single outgoing connection.
   */
  private processEntry(nodeId: string, node: StoryNode): TraversalResult {
    const outgoing = this.graph.connections.filter((c) => c.source === nodeId);
    if (outgoing.length === 0) {
      return {
        success: true,
        currentNodeId: nodeId,
      };
    }
    return this.makeChoice(outgoing[0].target);
  }

  /**
   * Process an Exit node
   *
   * Exit nodes mark the end of a nested graph and bubble up to the parent.
   * For now, this just marks the story as complete.
   */
  private processExit(nodeId: string, node: StoryNode): TraversalResult {
    if (this.currentState) {
      this.currentState.isComplete = true;
      this.currentState.completedAt = Date.now();
    }
    return {
      success: true,
      currentNodeId: nodeId,
    };
  }

  /**
   * Evaluate a condition expression
   *
   * Parses and evaluates the condition string against the current variable state.
   * Returns true/false or false on parse error.
   *
   * @param expression The condition expression to evaluate
   * @returns Boolean result of the condition
   */
  private evaluateCondition(expression: string): boolean {
    if (!this.currentState) return false;

    const parseResult = parseScript(expression);
    if (!parseResult.success) {
      console.error(`Condition parse error: ${parseResult.error}`);
      return false;
    }

    const evalResult = evaluate(parseResult.ast, {
      variables: this.currentState.variables,
      builtins: this.config.builtins,
    });

    if (!evalResult.success) {
      console.error(`Condition evaluation error: ${evalResult.error}`);
      return false;
    }

    return Boolean(evalResult.value);
  }

  /**
   * Execute an instruction script
   *
   * Parses and evaluates the instruction against the current variable state.
   * The script may modify variables, which persist in the playthrough state.
   *
   * @param script The instruction script to execute
   * @returns Result with updated variables or error
   */
  private executeInstruction(script: string): {
    success: boolean;
    error?: string;
  } {
    if (!this.currentState) return { success: false, error: 'No active playthrough' };

    const parseResult = parseScript(script);
    if (!parseResult.success) {
      return { success: false, error: parseResult.error };
    }

    const evalResult = evaluate(parseResult.ast, {
      variables: this.currentState.variables,
      builtins: this.config.builtins,
    });

    if (!evalResult.success) {
      return { success: false, error: evalResult.error };
    }

    // Update variables from the evaluation
    if (evalResult.updatedVariables) {
      this.currentState.variables = evalResult.updatedVariables;
    }

    return { success: true };
  }

  /**
   * Get the current playthrough state
   *
   * Returns a snapshot of the current playthrough including all state,
   * history, and metadata. Suitable for saving to the database.
   *
   * @returns The current playthrough state
   */
  getCurrentPlaythrough(): PlaythroughState | null {
    return this.currentState ? { ...this.currentState } : null;
  }

  /**
   * Get coverage metrics for the playthrough
   *
   * Calculates what percentage of nodes have been visited.
   * Useful for reporting story completion and finding unreachable content.
   *
   * @returns Coverage percentage (0-100)
   */
  getCoverage(): number {
    if (this.graph.nodes.size === 0) return 0;
    return (this.visitedNodeIds.size / this.graph.nodes.size) * 100;
  }

  /**
   * Get the list of visited node IDs
   *
   * @returns Set of node IDs that were visited in the current playthrough
   */
  getVisitedNodes(): Set<string> {
    return new Set(this.visitedNodeIds);
  }

  /**
   * Reset the current playthrough
   *
   * Clears all state and visited nodes. Used when starting a new playthrough.
   */
  reset(): void {
    this.currentState = null;
    this.stepCount = 0;
    this.visitedNodeIds.clear();
  }
}
